// Predefined exterior categories. Each project gets 9 items (all toggleable).
import {
  Home as HomeIcon, PaintBucket, DoorClosed, Car, Trees,
  Waves, Fence, Droplets, Shield,
} from "lucide-react";

// Scope multiplier applied to final cost
export const SCOPE_MULT = {
  repair: 0.4,
  replace: 1.0,
  upgrade: 1.4,
};

export const SCOPE_OPTIONS = [
  { value: "repair", label: "Repair" },
  { value: "replace", label: "Replace" },
  { value: "upgrade", label: "Upgrade" },
];

export const CONDITION_OPTIONS = [
  { value: "good", label: "Good" },
  { value: "tired", label: "Tired" },
  { value: "poor", label: "Poor" },
];

// Default per-category config.
// defaultQty: number OR function(floorAreaSqm) => number
// presets: optional list of variant presets for categories whose cost varies
//   meaningfully by type (doors, fences, security hardware, etc.).
//   When a category has presets, its line total is computed from `item.variants`
//   instead of a single qty × rate; presets are offered as one-click add buttons.
export const EXTERIOR_CATEGORIES = [
  { key: "roof",            label: "Roof",                unit: "sqm",  baseRate: 220,  defaultQty: (sqm) => Math.max(40, Math.round(sqm * 1.1)),  icon: HomeIcon },
  { key: "exterior_paint",  label: "Exterior Paint",      unit: "sqm",  baseRate: 45,   defaultQty: (sqm) => Math.max(80, Math.round(sqm * 1.8)),  icon: PaintBucket },
  {
    key: "windows_doors", label: "Windows & Doors", unit: "item", baseRate: 900,
    defaultQty: 8, icon: DoorClosed,
    presets: [
      { label: "Small window (fixed)",     rate: 600 },
      { label: "Medium window (sliding)",  rate: 900 },
      { label: "Large window (bay)",       rate: 1800 },
      { label: "Skylight",                 rate: 1500 },
      { label: "Interior single door",     rate: 450 },
      { label: "Exterior single door",     rate: 900 },
      { label: "Double door",              rate: 2000 },
      { label: "Sliding/stacking door",    rate: 4500 },
      { label: "Garage door",              rate: 1800 },
    ],
  },
  { key: "driveway",        label: "Driveway / Paving",   unit: "sqm",  baseRate: 180,  defaultQty: 30,  icon: Car },
  { key: "landscaping",     label: "Garden / Landscaping", unit: "sqm", baseRate: 90,   defaultQty: 80,  icon: Trees },
  { key: "pool",            label: "Pool",                unit: "item", baseRate: 15000, defaultQty: 1,  icon: Waves },
  {
    key: "boundary_walls", label: "Boundary Walls", unit: "lm", baseRate: 220,
    defaultQty: 40, icon: Fence,
    presets: [
      { label: "Brick wall (per lm)",           rate: 220 },
      { label: "Palisade (per lm)",             rate: 140 },
      { label: "Electric fence topper (per lm)", rate: 85 },
      { label: "Driveway gate + motor",         rate: 3500 },
    ],
  },
  { key: "drainage",        label: "Drainage",            unit: "item", baseRate: 3500, defaultQty: 1,   icon: Droplets },
  {
    key: "security", label: "Security", unit: "item", baseRate: 1800,
    defaultQty: 1, icon: Shield,
    presets: [
      { label: "Alarm system",           rate: 800 },
      { label: "Camera package (4 cams)", rate: 2500 },
      { label: "Camera package (8 cams)", rate: 4500 },
      { label: "Electric fence",         rate: 3000 },
      { label: "Gate automation",        rate: 1800 },
      { label: "Beams (set of 4)",       rate: 600 },
    ],
  },
];

function cryptoRandomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "ext-" + Math.random().toString(36).slice(2);
}

// Ensure project.exterior has all 9 categories (adds missing ones without mutating input).
export function ensureExteriorItems(existing = [], floorAreaSqm = 0) {
  const byKey = new Map(existing.map((i) => [i.category, i]));
  return EXTERIOR_CATEGORIES.map((c) => {
    const prev = byKey.get(c.key);
    if (prev) return prev;
    const qty = typeof c.defaultQty === "function" ? c.defaultQty(floorAreaSqm) : c.defaultQty;
    return {
      id: cryptoRandomId(),
      category: c.key,
      label: c.label,
      enabled: false,
      quantity: qty,
      unit: c.unit,
      condition: "tired",
      scope: "replace",
      rate: c.baseRate,
    };
  });
}

export function exteriorItemTotal(item) {
  if (!item || !item.enabled) return 0;
  const m = SCOPE_MULT[item.scope] ?? 1;
  if (Array.isArray(item.variants) && item.variants.length > 0) {
    const base = item.variants.reduce(
      (s, v) => s + (Number(v.qty) || 0) * (Number(v.rate) || 0),
      0
    );
    return base * m;
  }
  const q = Number(item.quantity) || 0;
  const r = Number(item.rate) || 0;
  return q * r * m;
}

export function exteriorTotal(items = []) {
  return items.reduce((s, i) => s + exteriorItemTotal(i), 0);
}

export function getCategoryMeta(key) {
  return EXTERIOR_CATEGORIES.find((c) => c.key === key);
}
